/**
 * Mobile product main-image Splide slider.
 * Inits on mobile viewports and tears down on desktop so resize works without refresh.
 *
 * Sync rules (all three stay in step, by media id, not by position):
 *  - Swipe the slider      -> highlight the matching thumbnail
 *  - Tap a thumbnail       -> slide to that image
 *  - Select a variant      -> the gallery marks the variant's thumbnail (aria-current),
 *                             and the slider follows it
 * Media ids are used because variant changes can reorder the gallery DOM, so list
 * positions stop matching the slider.
 *
 * This file is loaded by both theme.liquid (as product-mobile-gallery.js) and
 * main-product (as product-thumbnail-slider.js); the guard below keeps it to one instance.
 */
(function () {
  if (window.__hmMobileGallery) return;
  window.__hmMobileGallery = true;

  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');
  let splideInstance = null;
  let slideIds = [];
  let syncing = false;
  let thumbObserver = null;

  function getOriginalList() {
    return document.querySelector('.product__media-list');
  }

  function getThumbnailList() {
    return document.querySelector("[id^='Slider-Thumbnails-']");
  }

  function getThumbnails() {
    const list = getThumbnailList();
    return list ? list.querySelectorAll('.thumbnail-list__item') : [];
  }

  function markThumbnail(mediaId) {
    getThumbnails().forEach((thumb) => {
      const active = thumb.dataset.target === mediaId;
      thumb.classList.toggle('is-active', active);
      const button = thumb.querySelector('button');
      if (!button) return;
      if (active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  }

  function markViewer(mediaId) {
    const list = getOriginalList();
    if (!list) return;
    list.querySelectorAll('[data-media-id]').forEach((item) => {
      item.classList.toggle('is-active', item.dataset.mediaId === mediaId);
    });
  }

  function goToMedia(mediaId) {
    if (!splideInstance || !mediaId) return;
    let index = slideIds.indexOf(mediaId);
    if (index < 0) {
      // The variant brought in media the slider doesn't have yet: rebuild, then go.
      initMobileGallery(mediaId);
      return;
    }
    if (splideInstance.index !== index) {
      syncing = true;
      splideInstance.go(index);
      syncing = false;
    }
    markThumbnail(mediaId);
  }

  function onMove(newIndex) {
    const mediaId = slideIds[newIndex];
    if (!mediaId) return;
    markThumbnail(mediaId);
    markViewer(mediaId);
  }

  function watchThumbnails() {
    if (thumbObserver) thumbObserver.disconnect();
    const list = getThumbnailList();
    if (!list || !window.MutationObserver) return;
    thumbObserver = new MutationObserver((mutations) => {
      if (!splideInstance || syncing) return;
      for (const m of mutations) {
        if (m.attributeName === 'aria-current' && m.target.hasAttribute('aria-current')) {
          const item = m.target.closest('.thumbnail-list__item');
          if (item && item.dataset.target !== slideIds[splideInstance.index]) {
            goToMedia(item.dataset.target);
          }
          break;
        }
      }
    });
    thumbObserver.observe(list, { attributes: true, subtree: true, attributeFilter: ['aria-current'] });
  }

  function destroyMobileGallery() {
    if (thumbObserver) {
      thumbObserver.disconnect();
      thumbObserver = null;
    }
    if (splideInstance) {
      try {
        splideInstance.destroy(true);
      } catch (e) {
        /* already destroyed */
      }
      splideInstance = null;
    }
    slideIds = [];

    document.querySelectorAll('.custom-splide').forEach((el) => el.remove());

    const originalList = getOriginalList();
    if (originalList) {
      originalList.style.display = '';
      originalList.removeAttribute('aria-hidden');
    }
  }

  function buildSplideFromList(originalList) {
    const items = Array.from(originalList.querySelectorAll('li[data-media-id]')).filter((li) =>
      li.querySelector('img')
    );
    if (items.length <= 1) return null;

    const splide = document.createElement('div');
    splide.className = 'splide custom-splide';

    const track = document.createElement('div');
    track.className = 'splide__track';

    const ul = document.createElement('ul');
    ul.className = 'splide__list';

    slideIds = [];
    items.forEach((item) => {
      const img = item.querySelector('img');
      const li = document.createElement('li');
      li.className = 'splide__slide';
      li.dataset.mediaId = item.dataset.mediaId;

      const cleanImg = document.createElement('img');
      cleanImg.src = img.currentSrc || img.src;
      cleanImg.alt = img.alt || '';
      cleanImg.loading = 'lazy';
      cleanImg.style.width = '100%';
      cleanImg.style.display = 'block';

      li.appendChild(cleanImg);
      ul.appendChild(li);
      slideIds.push(item.dataset.mediaId);
    });

    track.appendChild(ul);
    splide.appendChild(track);

    return splide;
  }

  function currentMediaId() {
    const active = document.querySelector("[id^='Slider-Thumbnails-'] .thumbnail[aria-current]");
    if (active) return active.closest('.thumbnail-list__item').dataset.target;
    const list = getOriginalList();
    const activeItem = list && list.querySelector('[data-media-id].is-active');
    return activeItem ? activeItem.dataset.mediaId : null;
  }

  function initMobileGallery(startMediaId) {
    if (!MOBILE_MQ.matches || typeof Splide === 'undefined') return;

    const wanted = startMediaId || currentMediaId();
    destroyMobileGallery();

    const originalList = getOriginalList();
    if (!originalList) return;

    const splideEl = buildSplideFromList(originalList);
    if (!splideEl) return;

    originalList.parentNode.insertBefore(splideEl, originalList);
    originalList.style.display = 'none';

    const startIndex = Math.max(0, slideIds.indexOf(wanted));
    splideInstance = new Splide(splideEl, {
      type: 'slide',
      perPage: 1,
      gap: '12px',
      arrows: false,
      pagination: false,
      drag: true,
      start: startIndex,
    });

    splideInstance.on('move', onMove);
    splideInstance.mount();
    onMove(splideInstance.index);
    watchThumbnails();
  }

  function handleBreakpointChange(event) {
    if (event.matches) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => initMobileGallery());
      });
    } else {
      destroyMobileGallery();
    }
  }

  function boot() {
    MOBILE_MQ.addEventListener('change', handleBreakpointChange);

    document.addEventListener('click', (event) => {
      if (!splideInstance || !MOBILE_MQ.matches) return;

      const thumb = event.target.closest("[id^='Slider-Thumbnails-'] .thumbnail-list__item");
      if (!thumb) return;

      event.preventDefault();
      goToMedia(thumb.dataset.target);
    });

    if (MOBILE_MQ.matches) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => initMobileGallery());
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    destroyMobileGallery();
    if (MOBILE_MQ.matches) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => initMobileGallery());
      });
    }
  });
})();
